import React, { useState } from 'react';

import APFormGroup from 'components/FormFields/APFormGroup';
import FormSelect from 'components/FormFields/FormSelect';
import InputField from 'components/FormFields/InputField';
import ImageUploadField from 'components/FormFields/ImageUploadField';

import { MAX_NOTE_NAME_LENGTH, NOTE_RESTRICTIONS } from 'utils/constants';
import clsx from 'clsx';

const CreateNoteTemplateSettings = ({ type, createNoteTemplate, closeNoteModal }) => {
  const [noteTitle, setNoteTitle] = useState('');
  const [restriction, setRestriction] = useState('None');

  const [imageKey, setImageKey] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [originalImageKey, setOriginalImageKey] = useState(null);

  const [shortcutUrl, setShortcutUrl] = useState('');
  const [isShortcutUrlValid, setShortcutUrlValid] = useState(true);

  const [error, setError] = useState('');
  const [isFormValid, setFormValid] = useState(true);

  const validateForm = () => {
    if ((type === 0 && (!shortcutUrl || !noteTitle))
      || (type === 1 && !noteTitle)) {
      setError('Please complete all required fields above.');
      setFormValid(false);
      return false;
    }
    /* Removing url validation for now. May want to add it back later.
    // This pattern failed validation https://url.com/subpage?page=argument#/collaboration/uuid
    if (type === 0 && !isUrlValid(shortcutUrl)) {
      setError('Please review and update your website format to continue.');
      setShortcutUrlValid(false);
      return false;
    }
    */
    setError('');
    setFormValid(true);
    return true;
  };

  const handleImageUploadError = (value) => {
    const { acceptType } = value || {};
    if (acceptType) {
      setError('Please upload your image in one of the following formats: JPEG or PNG.');
    }
    else {
      setError('Something went wrong with uploading your image. Please try again.');
    }
  };

  const handleNoteTemplateCreate = async (e) => {
    e.currentTarget.disabled = true;
    if (validateForm()) {
      const noteBody = {
        title: noteTitle,
        plannerOnly: (restriction === 'VisibleToOrgMembersOnly'),
        clientReadOnly: restriction === ('ClientEditingLocked'),
        imageKey,
        url: shortcutUrl,
      };
      createNoteTemplate(noteBody);
      // Don't close the modal because the saga will redirect if it's not a shortcut.
      if (!shortcutUrl) return;
      closeNoteModal();
    } else {
      e.currentTarget.disabled = false;
    }
  };

  const renderNoteTitle = () => {
    return (
      <InputField
        name="note_title"
        label="Title"
        value={noteTitle}
        onChange={(event) => {
          setNoteTitle(event.target.value);
          setError('');
        }}
        showError={!isFormValid && !noteTitle}
        maxLength={MAX_NOTE_NAME_LENGTH}
        required
      />
    );
  };

  const renderShortcut = () => {
    return (
      <InputField
        name="shortcut_url"
        label="Url"
        value={shortcutUrl}
        onChange={(event) => {
          setShortcutUrl(event.target.value);
          setShortcutUrlValid(true);
          setFormValid(true);
          setError('');
        }}
        onBlur={() => {
          if (
            shortcutUrl &&
            !shortcutUrl.includes('https://')
          ) {
            setShortcutUrl(`https://${shortcutUrl}`);
          }
        }}
        showError={!isFormValid && (!shortcutUrl) || !isShortcutUrlValid}
        required
      />
    );
  };

  const renderRestrictions = (noteType) => {
    return (
      <FormSelect
        id="restrictions"
        name="Restrictions"
        defaultValue="None"
        value={restriction}
        onChange={(e) => {
          setRestriction(e.target.value);
          if (e.target.value === 'ClientEditingLocked' && noteType === 0) {
            setError(<>Heads up! Although client editing is locked for this shortcut in Aisle Planner, you may
              <br /> still need to manage outside permissions separately.</>);
          } else {
            setError('');
          }
        }}
      >
        {NOTE_RESTRICTIONS.map((option, index) => (
          <option value={option.value}
            key={`option_${index}`}
          >
            {option.label}
          </option>
        ))}
      </FormSelect>
    );
  };

  const renderCoverImage = () => {
    return (
      <ImageUploadField
        label='Cover image'
        imageUploadType='shortcut'
        imageUrl={imageUrl}
        originalImageKey={originalImageKey}
        updateImageKey={setImageKey}
        updateImageUrl={setImageUrl}
        updateOriginalImageKey={setOriginalImageKey}
        imageInfoPath='wedding/note/images'
        imageProcessPath='wedding/note/image'
        imageCropperUploadText='Drag your image here'
        errorCallback={handleImageUploadError}
      />
    );
  };

  const actionText = (noteType) => {
    if (noteType === 0) {
      return ('Create shortcut');
    }
    return ('Create note');
  };

  const createNoteContent = (noteType) => {
    if (noteType === 0) {
      return (
        <>
          {renderNoteTitle()}
          {renderShortcut()}
          {renderRestrictions(noteType)}
          {renderCoverImage()}
        </>
      );
    }
    if (noteType === 1) {
      return (
        <>
          {renderNoteTitle()}
          {renderRestrictions(noteType)}
          {renderCoverImage()}
        </>
      );
    }
  };

  return (
    <>
      <APFormGroup grid={false}>
        {createNoteContent(type)}
      </APFormGroup>
      <p className="note-error mb-3">
        {error}
      </p>
      <button
        className={clsx('button', type === 2 && 'create-note-modal__button')}
        onClick={handleNoteTemplateCreate}
      >
        {actionText(type)}
      </button>
    </>
  );
};

export default CreateNoteTemplateSettings;
